import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';
import { useGetExportConnectionsQuery } from '@verantos-dev/data-access-export-connections';
import {
  ActionMenu,
  type ActionMenuItemProps,
  type IconType,
} from '@verantos-dev/shared-components';
import { type Cohort, CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useCohortModalContext } from '../../context/modalContext';

type CohortListActionMenuProps = {
  cohort: Cohort;
};

export const CohortsListActionMenu: FC<CohortListActionMenuProps> = ({ cohort }) => {
  const isEnableExportConnection = useFeatureFlag('RLORhidedisablecohortexportFS');
  const isCopyCohortEnabled = useFeatureFlagSplit('copy-cohort', false);

  const intl = useIntl();
  const {
    openDeleteCohortModal,
    openRenameCohortModal,
    openExportCohortModal,
    openCopyCohortModal,
  } = useCohortModalContext();

  const {
    user: { userId, auth0Id, organizationId },
  } = useAuth();
  const isAuthorizedToRenameDelete = [userId, auth0Id].includes(cohort.createdBy.userId);
  const isDisabledDeleteStatus =
    cohort.latestStatus === CohortDefinitionStatus.PROCESSING ||
    cohort.latestStatus === CohortDefinitionStatus.FINAL;

  const isDisabledExportStatus =
    cohort.latestStatus !== CohortDefinitionStatus.PROCESSED &&
    cohort.latestStatus !== CohortDefinitionStatus.FINAL;

  const isDisableExportForUnExportableCohort =
    cohort.latestSubjectCount > 3000 || cohort.latestSubjectCount === 0;

  const { data: exportConnectionData } = useGetExportConnectionsQuery({
    organizationId: organizationId,
  });

  const isExportConnectionAvailableForUser =
    exportConnectionData && exportConnectionData?.length > 0;

  const menu: ActionMenuItemProps[] = [
    ...(isAuthorizedToRenameDelete
      ? [
          {
            id: `cohortActionMenu-renameCohort-${cohort.cohortProfileId}`,
            label: intl.formatMessage({ id: 'cohortsList.actionMenu.rename.label' }),
            onClick: () => openRenameCohortModal(cohort.cohortProfileId),
            icon: 'rename',
          },
        ]
      : []),
    ...(isCopyCohortEnabled
      ? [
          {
            id: `cohortActionMenu-copyCohort-${cohort.cohortProfileId}`,
            label: intl.formatMessage({ id: 'cohortsList.actionMenu.copy.label' }),
            onClick: () =>
              openCopyCohortModal(
                cohort.cohortProfileId,
                cohort.cohortName,
                cohort.latestCohortVersionId
              ),
            icon: 'copy',
          },
        ]
      : []),
    {
      id: `cohortActionMenu-exportCohort-${cohort.cohortProfileId}`,
      label: intl.formatMessage({ id: 'cohortsList.actionMenu.export.label' }),
      disabled:
        !isEnableExportConnection ||
        isDisabledExportStatus ||
        !isExportConnectionAvailableForUser ||
        isDisableExportForUnExportableCohort,
      onClick: () => openExportCohortModal(cohort.cohortProfileId),
      icon: 'export',
    },
    ...(isAuthorizedToRenameDelete
      ? [
          {
            id: `cohortActionMenu-deleteCohort-${cohort.cohortProfileId}`,
            label: intl.formatMessage({ id: 'cohortsList.actionMenu.delete.label' }),
            disabled: isDisabledDeleteStatus,
            onClick: () => openDeleteCohortModal(cohort.cohortProfileId),
            icon: 'delete',
            iconDisabled: 'deleteBlack' as IconType,
          },
        ]
      : []),
  ];

  return <ActionMenu id={`cohortListV2ActionMenu`} menuItems={menu} stopClickEventPropagation />;
};
